import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { WaitlistModal } from "@/pages/landing/WaitlistModal";
import { openBooking, BOOKING_URL, trackBooking } from "@/lib/booking";

export const SITE_NAV = [
  { label: "Occasions", to: "/occasions" },
  { label: "Panchanga", to: "/panchanga" },
  { label: "Astrology", to: "/astrology" },
  { label: "For Patashala Students", to: "/patashala" },
  { label: "Stotras", to: "/stotras" },
  { label: "Heritage", to: "/heritage" },
  { label: "Festival Calendar", to: "/festivals" },
  { label: "Blogs", to: "/blog" },
];

export const SiteHeader = ({ openWaitlist }) => {
  const [menu, setMenu] = useState(false);
  const [selfOpen, setSelfOpen] = useState(false);
  const trigger = (location) => openBooking({ location });
  return (
    <header className="sticky top-0 z-50 border-b border-[#A8722A]/20 bg-[#FBF6EC]/95 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8">
        <Link to="/" className="flex items-center" data-testid="site-logo" aria-label="PurohithConnect™ home">
          <img src="/brand/pc-wordmark.png" alt="PurohithConnect™" className="h-7 w-auto sm:h-8" />
        </Link>
        <nav className="hidden items-center gap-6 lg:flex" aria-label="Main navigation">
          {SITE_NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-mukta text-sm font-medium text-[#6B5044] transition-colors hover:text-[#3B221B] focus:outline-none focus:ring-2 focus:ring-[#A8722A]/50 rounded">
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2.5">
          <button onClick={() => trigger("site-header")} data-testid="site-early-access-btn"
            className="hidden rounded-full bg-[#B23A2B] px-5 py-2 font-mukta text-sm font-semibold text-[#FFFDF8] transition-colors hover:bg-[#9a2f22] focus:outline-none focus:ring-2 focus:ring-[#B23A2B]/50 sm:block">
            Book now
          </button>
          <button className="lg:hidden rounded p-1.5 text-[#3B221B] focus:outline-none focus:ring-2 focus:ring-[#A8722A]/50" onClick={() => setMenu(!menu)}
            aria-label={menu ? "Close menu" : "Open menu"} data-testid="mobile-menu-btn">
            {menu ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {menu && (
        <nav className="border-t border-[#A8722A]/15 bg-[#FBF6EC] px-6 py-4 lg:hidden" aria-label="Mobile navigation" data-testid="mobile-menu">
          {SITE_NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setMenu(false)} className="block py-2 font-mukta text-[#3B221B]">{n.label}</Link>
          ))}
          <button onClick={() => { setMenu(false); trigger("site-header-mobile"); }} data-testid="mobile-early-access-btn"
            className="mt-2 w-full rounded-full bg-[#B23A2B] px-5 py-2 font-mukta text-sm font-semibold text-[#FFFDF8]">Book now</button>
        </nav>
      )}
      {!openWaitlist && (
        <WaitlistModal key={selfOpen} open={selfOpen} defaultRole="Family" onClose={() => setSelfOpen(false)} />
      )}
    </header>
  );
};

export const SiteFooter = () => (
  <footer className="border-t border-[#A8722A]/20 bg-[#F3EADA]">
    <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-6 font-mukta text-sm text-[#6B5044] sm:flex-row md:px-8">
      <div className="flex flex-col items-center gap-2 sm:items-start">
        <img src="/brand/pc-wordmark.png" alt="PurohithConnect™" className="h-6 w-auto" data-testid="footer-logo" />
        <p data-testid="footer-copyright">© 2026 PurohithConnect™ · Built in Bengaluru, Karnataka — for India</p>
      </div>
      <div className="flex flex-wrap justify-center gap-4">
        <Link to="/occasions" className="hover:text-[#3B221B]">Occasions</Link>
        <Link to="/parayana" className="hover:text-[#3B221B]">Parayana</Link>
        <Link to="/panchanga" className="hover:text-[#3B221B]" data-testid="footer-panchanga-link">Panchanga</Link>
        <Link to="/heritage" className="hover:text-[#3B221B]" data-testid="footer-heritage-link">Heritage Archive</Link>
        <Link to="/impact" className="hover:text-[#3B221B]" data-testid="footer-impact-link">Our Impact</Link>
        <Link to="/astrology" className="hover:text-[#3B221B]">Astrology</Link>
        <Link to="/patashala" className="hover:text-[#3B221B]" data-testid="footer-patashala-link">For Patashala Students</Link>
        <Link to="/traditions" className="hover:text-[#3B221B]">Traditions</Link>
        <Link to="/purohiths" className="hover:text-[#3B221B]">For Purohiths</Link>
        <Link to="/stotras" className="hover:text-[#3B221B]">Stotras</Link>
        <Link to="/festivals" className="hover:text-[#3B221B]">Festival Calendar</Link>
        <Link to="/rituals" className="hover:text-[#3B221B]">Rituals &amp; Vratas</Link>
        <Link to="/locations" className="hover:text-[#3B221B]" data-testid="footer-locations-link">Areas We Serve</Link>
        <Link to="/blog" className="hover:text-[#3B221B]" data-testid="footer-blog-link">Blogs</Link>
        <Link to="/contact" className="hover:text-[#3B221B]" data-testid="footer-contact-link">Contact</Link>
        <a href="https://wa.me/919110248636" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#128C3E] hover:text-[#0e6b2f]" data-testid="footer-whatsapp">WhatsApp</a>
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackBooking({ location: "site-footer" })} className="font-semibold text-[#B23A2B] hover:text-[#9a2f22]" data-testid="footer-book-link">Book a Purohith ↗</a>
      </div>
    </div>
  </footer>
);
